import TopNavSmallButton from "../CommonButtons/TopNavSmallButton";
/**
 * -----------------------------------------------------------------------------
 * CommonQuickActionButtons
 * -----------------------------------------------------------------------------
 *
 * A reusable component for rendering quick action buttons.
 *
 * Layout Rules:
 * - Accepts a flat `actions` array.
 * - Hidden actions (`hidden: true`) are automatically filtered out.
 * - Buttons are automatically grouped into rows of two.
 * - A single button in the last row occupies the full width.
 *
 * Example:
 *
 * <CommonQuickActionButtons
 *   title="Quick Actions"
 *   actions={[
 *     {
 *       key: "edit",
 *       btnText: (
 *         <>
 *           Edit
 *           <br />
 *           Account
 *         </>
 *       ),
 *       icon: ICONS.EDIT,
 *       handleFunction: handleEdit,
 *       isDisabled: false,
 *     },
 *     {
 *       key: "archive",
 *       btnText: (
 *         <>
 *           Archive
 *           <br />
 *           Account
 *         </>
 *       ),
 *       icon: ICONS.ARCHIVE,
 *       handleFunction: handleArchive,
 *       isDisabled: false,
 *     },
 *     {
 *       key: "history",
 *       btnText: (
 *         <>
 *           Transaction
 *           <br />
 *           History
 *         </>
 *       ),
 *       icon: ICONS.TRANSACTION_HISTORY,
 *       handleFunction: openHistory,
 *       isDisabled: false,
 *     },
 *   ]}
 * />
 *
 * -----------------------------------------------------------------------------
 * Props
 * -----------------------------------------------------------------------------
 *
 * @param {Object} props
 *
 * @param {string} [props.title="Quick Actions"]
 * Header displayed above the quick action section.
 *
 * @param {Array<Object>} [props.actions=[]]
 * Flat array of action objects. The component automatically filters hidden
 * actions and arranges them into rows of two.
 *
 * Action Object:
 *
 * @param {string} [key]
 * Optional unique key for React rendering.
 *
 * @param {React.ReactNode} btnText
 * Text or JSX displayed inside the button.
 *
 * @param {string} icon
 * Icon passed to TopNavSmallButton.
 *
 * @param {Function} handleFunction
 * Callback executed when the button is clicked.
 *
 * @param {boolean} [isDisabled=false]
 * Disables the button.
 *
 * @param {boolean} [hidden=false]
 * If true, the action is not rendered.
 *
 * @param {"button" | "submit"} [btnType="button"]
 * HTML button type.
 *
 * -----------------------------------------------------------------------------
 * Notes
 * -----------------------------------------------------------------------------
 *
 * - Uses TopNavSmallButton internally.
 * - Automatically applies common styling.
 * - Automatically filters hidden actions.
 * - Automatically creates rows of two buttons.
 * - Single remaining button spans the full width.
 * - Automatically renders dividers between buttons in the same row.
 *
 * -----------------------------------------------------------------------------
 */

const CommonQuickActionButtons = ({
  title = "Quick Actions",
  actions = [],
}) => {
  const visibleActions = actions.filter((action) => action && !action.hidden);

  const rows = [];

  for (let i = 0; i < visibleActions.length; i += 2) {
    rows.push(visibleActions.slice(i, i + 2));
  }

  return (
    <div className="flex flex-col w-full">
      <div className="py-padding-sm px-padding-md bg-primary-card text-font-sm text-text-primary font-semibold">
        {title}
      </div>

      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`grid ${
            row.length === 1 ? "grid-cols-1" : "grid-cols-2"
          } bg-primary-nav border-b border-primary-bg`}
        >
          {row.map((item, index) => {
            const { key, ...buttonProps } = item;

            return (
              <div
                key={key || index}
                className="relative"
              >
                <TopNavSmallButton
                  {...buttonProps}
                  btnType={buttonProps.btnType ?? "button"}
                  enabledTextColor="var(--text-primary)"
                  enabledBgColor="var(--primary-nav-act)"
                  disabledBgColor="var(--primary-nav-act)"
                  hoverOption="white"
                  fullWidthRequired
                />

                {index !== row.length - 1 && (
                  <div className="absolute top-top-base right-right-base h-full border-r border-primary-bg" />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default CommonQuickActionButtons;
